import React, { useEffect, useState } from 'react';
import { X, Loader2, FileText, Building2, User, Package, Calculator, CheckCircle } from 'lucide-react';
import api from '../../services/api';
import { formatarMoeda, formatarData, formatarCnpj } from '../../utils/formatters';

function Linha({ label, valor }) {
  if (!valor) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:gap-2">
      <span className="text-slate-500 text-xs w-28 flex-shrink-0">{label}</span>
      <span className="text-slate-200 text-xs">{valor}</span>
    </div>
  );
}

function Secao({ titulo, icon: Icon, children }) {
  return (
    <div className="bg-surface/50 border border-surface-border rounded-lg p-4 space-y-2">
      <div className="flex items-center gap-1.5 mb-3">
        {Icon && <Icon size={13} className="text-primary-400" />}
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{titulo}</span>
      </div>
      {children}
    </div>
  );
}

function enderecoStr(ent) {
  if (!ent?.logradouro) return null;
  return [ent.logradouro, ent.numero, ent.bairro, [ent.municipio, ent.uf].filter(Boolean).join('/')].filter(Boolean).join(' — ');
}

export default function DocumentoVisualizadorModal({ docId, onFechar }) {
  const [dados,   setDados]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro,    setErro]    = useState(null);

  useEffect(() => {
    if (!docId) return;
    setLoading(true);
    setErro(null);
    setDados(null);
    api.get(`/documentos/${docId}/visualizar`)
      .then(r => setDados(r.data))
      .catch(() => setErro('Não foi possível carregar o documento.'))
      .finally(() => setLoading(false));
  }, [docId]);

  useEffect(() => {
    const fn = e => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onFechar]);

  const doc    = dados?.documento;
  const emit   = dados?.emitente;
  const dest   = dados?.destinatario;
  const itens  = dados?.itens || [];
  const totais = dados?.totais;
  const prot   = dados?.protocolo;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onFechar} />

      <div className="relative w-full max-w-4xl bg-[#0a0f1e] border border-surface-border rounded-2xl shadow-2xl max-h-[92vh] flex flex-col">
        {/* Cabeçalho */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <FileText size={15} className="text-primary-400" />
            <span className="text-sm font-semibold text-slate-200">
              {doc ? `${doc.tipo?.toUpperCase()} nº ${doc.numero}/${doc.serie}` : 'Visualizar Documento'}
            </span>
            {doc && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                doc.situacao === 'autorizado' ? 'bg-emerald-900/60 text-emerald-300' :
                doc.situacao === 'cancelado'  ? 'bg-red-900/60 text-red-300' :
                'bg-yellow-900/60 text-yellow-300'
              }`}>{doc.situacao}</span>
            )}
          </div>
          <button onClick={onFechar}
            className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-surface-card transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Corpo */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={24} className="animate-spin text-primary-500" />
            </div>
          )}

          {erro && <div className="text-center py-20 text-red-400 text-sm">{erro}</div>}

          {!loading && !erro && dados && (
            <>
              {/* Emitente */}
              {emit && (
                <Secao titulo="Emitente" icon={Building2}>
                  <Linha label="Razão Social" valor={emit.razao} />
                  <Linha label="CNPJ"         valor={formatarCnpj(emit.cnpj)} />
                  <Linha label="Insc. Estadual" valor={emit.ie} />
                  <Linha label="Endereço"     valor={enderecoStr(emit)} />
                </Secao>
              )}

              {/* Destinatário */}
              {dest && (dest.razao || dest.cnpj) && (
                <Secao titulo="Destinatário" icon={User}>
                  <Linha label="Razão Social" valor={dest.razao} />
                  <Linha label="CNPJ/CPF"     valor={formatarCnpj(dest.cnpj)} />
                  <Linha label="Insc. Estadual" valor={dest.ie} />
                  <Linha label="Endereço"     valor={enderecoStr(dest)} />
                </Secao>
              )}

              {/* Itens */}
              {itens.length > 0 && (
                <div className="bg-surface/50 border border-surface-border rounded-lg overflow-hidden">
                  <div className="flex items-center gap-1.5 px-4 py-3 border-b border-surface-border">
                    <Package size={13} className="text-primary-400" />
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Itens ({itens.length})
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-surface/40 text-slate-500 font-medium">
                          <th className="px-3 py-2 text-left">#</th>
                          <th className="px-3 py-2 text-left">Descrição</th>
                          <th className="px-3 py-2 text-left">NCM</th>
                          <th className="px-3 py-2 text-left">CFOP</th>
                          <th className="px-3 py-2 text-right">Qtd</th>
                          <th className="px-3 py-2 text-right">Vl. Unit.</th>
                          <th className="px-3 py-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-surface-border">
                        {itens.map((item, i) => (
                          <tr key={i} className="hover:bg-surface-card/30">
                            <td className="px-3 py-2 text-slate-500">{item.numero || i + 1}</td>
                            <td className="px-3 py-2 text-slate-200 max-w-[200px]">
                              <p className="truncate" title={item.descricao}>{item.descricao}</p>
                            </td>
                            <td className="px-3 py-2 text-slate-400 font-mono">{item.ncm}</td>
                            <td className="px-3 py-2 text-slate-400 font-mono">{item.cfop}</td>
                            <td className="px-3 py-2 text-right text-slate-300">
                              {item.quantidade?.toLocaleString('pt-BR', { maximumFractionDigits: 4 })} {item.unidade}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-300">{formatarMoeda(item.valorUnitario)}</td>
                            <td className="px-3 py-2 text-right text-slate-200 font-medium">{formatarMoeda(item.valorTotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Totais + Protocolo */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {totais && totais.vNF > 0 && (
                  <Secao titulo="Totais" icon={Calculator}>
                    {totais.vBC     > 0 && <Linha label="Base ICMS" valor={formatarMoeda(totais.vBC)} />}
                    {totais.vICMS   > 0 && <Linha label="ICMS"      valor={formatarMoeda(totais.vICMS)} />}
                    {totais.vIPI    > 0 && <Linha label="IPI"       valor={formatarMoeda(totais.vIPI)} />}
                    {totais.vPIS    > 0 && <Linha label="PIS"       valor={formatarMoeda(totais.vPIS)} />}
                    {totais.vCOFINS > 0 && <Linha label="COFINS"    valor={formatarMoeda(totais.vCOFINS)} />}
                    {totais.vFrete  > 0 && <Linha label="Frete"     valor={formatarMoeda(totais.vFrete)} />}
                    {totais.vDesc   > 0 && <Linha label="Desconto"  valor={formatarMoeda(totais.vDesc)} />}
                    <div className="pt-2 border-t border-surface-border">
                      <Linha label="Total NF" valor={formatarMoeda(totais.vNF)} />
                    </div>
                  </Secao>
                )}

                {(prot?.numero || doc?.chave_acesso) && (
                  <Secao titulo="Autorização" icon={CheckCircle}>
                    {prot?.numero && <Linha label="Protocolo"  valor={prot.numero} />}
                    {prot?.data   && <Linha label="Data Auth." valor={formatarData(prot.data)} />}
                    {doc?.data_emissao && <Linha label="Emissão"   valor={formatarData(doc.data_emissao)} />}
                    {doc?.chave_acesso && (
                      <div className="mt-2">
                        <span className="text-slate-500 text-xs block mb-1">Chave de Acesso</span>
                        <span className="font-mono text-[10px] text-slate-400 break-all leading-relaxed">
                          {doc.chave_acesso}
                        </span>
                      </div>
                    )}
                  </Secao>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
